import { streamFromEndpoint } from './stream';
import type { CommandResult, StreamableResult } from './commands/types';
import type Anthropic from '@anthropic-ai/sdk';
import { captureEvent } from '$lib/analytics/posthog';

export type HistoryLine = { type: 'input' | 'output' | 'error' | 'ascii'; text: string };

export interface HandleResultDeps {
	history: HistoryLine[];
	chatMessages: Anthropic.MessageParam[];
	scrollToBottom: () => void;
	setHistory: (h: HistoryLine[]) => void;
	setChatMessages: (m: Anthropic.MessageParam[]) => void;
	setActiveOverlay: (name: string | null) => void;
	setTimerInitialTime: (t: string | undefined) => void;
	setStreaming: (streaming: boolean) => void;
	applyEffect: (effect: string) => void;
	onClose: () => void;
}

export async function handleCommandResult(
	result: CommandResult,
	deps: HandleResultDeps
): Promise<void> {
	const { history, setHistory, setActiveOverlay, setTimerInitialTime, applyEffect, onClose } = deps;

	switch (result.type) {
		case 'text':
		case 'theme':
			if (result.lines) {
				setHistory([
					...history,
					...result.lines.map((line) => ({ type: 'output' as const, text: line }))
				]);
			}
			break;

		case 'ascii':
			if (result.content) {
				setHistory([...history, { type: 'ascii', text: result.content }]);
			}
			break;

		case 'game':
			if (result.game) {
				setActiveOverlay(result.game);
				captureEvent('terminal_game_started', { game: result.game });
			}
			break;

		case 'animation':
			if (result.animation) {
				setActiveOverlay(result.animation);
				captureEvent('terminal_animation_started', { animation: result.animation });
			}
			break;

		case 'tool':
			if (result.tool) {
				if (result.tool === 'timer' && result.lines?.[0]) {
					setTimerInitialTime(result.lines[0]);
				}
				setActiveOverlay(result.tool === 'json' ? 'json' : result.tool);
				captureEvent('terminal_tool_used', { tool: result.tool });
			}
			break;

		case 'effect':
			if (result.effect) applyEffect(result.effect);
			break;

		case 'review':
			await handleStreamResult(
				result,
				'/api/review',
				'Reviewing code with Opus 4.5...',
				'AI review',
				deps
			);
			break;

		case 'chat':
			await handleStreamResult(result, '/api/chat', 'Thinking...', 'AI chat', deps);
			break;

		case 'clear':
			setTimeout(() => setHistory([]), 50);
			break;

		case 'exit':
			if (result.lines) {
				setHistory([
					...history,
					...result.lines.map((line) => ({ type: 'output' as const, text: line }))
				]);
			}
			setTimeout(onClose, 200);
			break;
	}
}

async function handleStreamResult(
	result: StreamableResult,
	endpoint: string,
	loadingText: string,
	label: string,
	deps: HandleResultDeps
): Promise<void> {
	const { history, chatMessages, scrollToBottom, setHistory, setChatMessages, setStreaming } = deps;
	const userMsg = result.lines[0];
	if (!userMsg) return;

	const updated = [...history, { type: 'output' as const, text: loadingText }];
	setHistory(updated);
	const streamIdx = updated.length - 1;
	scrollToBottom();
	setStreaming(true);

	const isChat = endpoint === '/api/chat';
	const body = isChat ? { messages: chatMessages, message: userMsg } : { code: userMsg };

	try {
		await streamFromEndpoint(
			endpoint,
			body,
			(fullText) => {
				// Direct index mutation — Svelte 5 $state tracks this
				deps.history[streamIdx] = { type: 'output', text: fullText };
				scrollToBottom();
			},
			(errorMsg) => {
				deps.history[streamIdx] = { type: 'error', text: errorMsg || `${label} unavailable.` };
			},
			isChat
				? (fullText) => {
						setChatMessages([
							...chatMessages,
							{ role: 'user', content: userMsg },
							{ role: 'assistant', content: fullText }
						]);
					}
				: undefined
		);
	} catch {
		deps.history[streamIdx] = {
			type: 'error',
			text: `${label} not configured. Set ANTHROPIC_API_KEY to enable.`
		};
	}
	setStreaming(false);
	scrollToBottom();
}
